import React, {useRef, useEffect, useState, useCallback} from 'react'
import {Box, Flex} from '@chakra-ui/react'
import PropTypes from 'prop-types'
import {useIntl} from 'react-intl'
import {ChevronLeftIcon, ChevronRightIcon} from '@salesforce/retail-react-app/app/components/icons'

import {getGDIHideStyles} from '../../utils/gdi-styles'
import {getAllExpandedFromContentArea} from '../../utils/gdi-content-area'
import {mapResponsiveValue, hasValue} from '../../utils/gdi-responsive'
import {renderComponentsSwitch} from '../../../utils/contentRenderer'
import {useHeaderContext} from '../../../../../contexts/header-context'
import {categoryUrlBuilder} from '../../../../../utils/url'
import {
    GDIHoverRevealContext,
    GDIGridColumnsContext,
    GDIProductTogglesContext,
    useGDIGridColumns,
    useIsInsideGroupedCategories
} from '../../utils/gdi-link-context'
import {useDragScroll} from '../../utils/use-drag-scroll'
import {useInfiniteLoop, maxResponsive} from '../../utils/use-infinite-loop'

import {
    buildSlideWidth,
    buildSpanWidth,
    getTileColSpan,
    slideMeta,
    getSlideStride,
    FLUID_INSET,
    parseBoxSides,
    isRecentlyViewedSource,
    isEarlyAccessSource,
    dynamicSourceToAIModel
} from './helpers'
import {ArrowButton} from './arrows'
import CarouselGridSkeleton from './carousel-grid-skeleton'
import {useCarouselGridConfig} from './use-carousel-grid-config'
import {useEdgeState} from './use-edge-state'
import {useAutoplay} from './use-autoplay'
import {useVickyTiles} from './use-vicky-tiles'
import {useRecentlyViewedTiles} from './use-recently-viewed-tiles'
import {useEarlyAccessTiles} from './use-early-access-tiles'
import {useSfccRecommendationTiles} from './use-sfcc-recommendation-tiles'
import {useProductTileToggles} from './use-product-tile-toggles'
import {useViewportBleed} from './use-viewport-bleed'
import {useGDIDynamicContentContext} from '../../utils/gdi-dynamic-content-context'
import {REWARD_TYPE_EXCLUSIVE_REWARDS} from '../../utils/gdi-reward-tile-context'
import {useLoyaltyData} from '../../../../../contexts/loyalty-data-context'
import {useCategory, useProduct} from '@salesforce/commerce-sdk-react'

const PLP_SUB_CATEGORIES_DYNAMIC_CONTENT = 'Sub-categories Links'
const PDP_MORE_TO_DISCOVER_DYNAMIC_CONTENT = 'More to Discover'
const GDI_PDP_PAGE_TYPE = 'GDIPDPPage'
const RESPONSIVE_BREAKPOINTS = ['base', 'md', 'lg']

const resolveResponsiveAt = (value, bp, fallback) => {
    if (value && typeof value === 'object') {
        if (value[bp] !== undefined) return value[bp]
        const index = RESPONSIVE_BREAKPOINTS.indexOf(bp)
        for (let i = index - 1; i >= 0; i -= 1) {
            const previous = value[RESPONSIVE_BREAKPOINTS[i]]
            if (previous !== undefined) return previous
        }
        return fallback
    }

    return value ?? fallback
}

const mapCarouselInteraction = (displayTypeByBp, draggableByBp, transform) =>
    Object.fromEntries(
        RESPONSIVE_BREAKPOINTS.map((bp) => [
            bp,
            transform(
                resolveResponsiveAt(displayTypeByBp, bp, 'Grid'),
                resolveResponsiveAt(draggableByBp, bp, true)
            )
        ])
    )

const normalizeProductSku = (value) => {
    if (typeof value === 'string') return value.trim() || null
    return value || null
}

const getPdpProductSku = (product) =>
    normalizeProductSku(product?.id || product?.productId || product?.master?.masterId)

const parseDiscoverCategories = (discoverCategories) => {
    try {
        const parsed = JSON.parse(discoverCategories || '[]')
        return Array.isArray(parsed) ? parsed : []
    } catch {
        return []
    }
}

const GDICarouselGridBlock = ({
    HideOnDevices,
    HideBlockOn,
    ContentTiles: ContentTilesProp,
    CtaButtonDynamicContent,
    CtaButtonDynamicContentReference,
    CtaButtonManualContent,
    CtaButtonStylesPerViewType,
    ProductCarouselGridManualContent,
    ProductCarouselGridContentTile: ProductCarouselGridContentTileProp,
    ProductCarouselGridDynamicContent,
    ProductCarouselGridDynamicContentReference,
    GDICarouselGridBlockStylesPerViewType,
    ProductCarouselGridStylesPerViewType,
    ReverseOrderOnDesktop,
    MaxItemsToShow,
    DisplayTextAndCTAsOnHover,
    ContentTilesStyles,
    _recommendationData
}) => {
    const {locale} = useIntl()
    const isFrCA = locale?.toLowerCase().replace('_', '-') === 'fr-ca'
    // Graph's expansion-depth cap used to truncate these arrays; that is now
    // repaired server-side before caching — see utils/optimizely-depth-repair.js.
    const ContentTiles = ContentTilesProp
    const ProductCarouselGridContentTile = ProductCarouselGridContentTileProp
    const effectiveStylesPerViewType = GDICarouselGridBlockStylesPerViewType
    const hideStyles = getGDIHideStyles(HideOnDevices || HideBlockOn)
    const scrollRef = useRef(null)
    const leftArrowRef = useRef(null)
    const rightArrowRef = useRef(null)
    /* `category` flows in from the active PLP/PDP page via HeaderContext.
     * Read unconditionally to keep hook order stable; `null` when the block
     * isn't placed on a PLP. */
    const {category} = useHeaderContext() || {}
    const {
        pdpProduct: contextPdpProduct,
        pdpProductSku: contextPdpProductSku,
        dynamicProductReference: contextDynamicProductReference,
        contentPageType,
        disableViewportBleed: contextDisableViewportBleed,
        forceCarouselBleed: contextForceCarouselBleed,
        carouselPeekInset: contextCarouselPeekInset
    } = useGDIDynamicContentContext()
    const isGDIPDPPageContext = contentPageType === GDI_PDP_PAGE_TYPE || Boolean(contextPdpProduct)
    const pdpProduct = isGDIPDPPageContext ? contextPdpProduct : null

    /* True when this block is rendered inside the mobile menu's
     * GroupedCategories drawer (MenuType === 'Custom'). In that scope the
     * `Sub-categories Links` CTA source omits the current/parent category
     * link and emits only the child subcategory links. */
    const isInsideGroupedCategories = useIsInsideGroupedCategories()

    const {
        displayTypeByBp,
        currentDisplayType,
        tilesPerRow,
        tilesPerRowAuto,
        hasPartialTilePeek,
        tilesGap,
        justify,
        alignItems,
        ctaAlignSelf,
        showArrows,
        arrowSx,
        arrowDisplay,
        draggable,
        draggableByBp,
        autoplay,
        loop,
        delay,
        tilesToScroll,
        sectionWidth,
        sectionBackgroundColor,
        sectionBackgroundMedia,
        sectionTitle,
        sectionColSpan,
        sectionGridColumn,
        sectionMargin,
        sectionPadding,
        isFluid,
        isFullBleed,
        hasMaxWidth,
        isFluidByBp,
        isFullBleedByBp,
        hasMaxWidthByBp
    } = useCarouselGridConfig(effectiveStylesPerViewType)

    /* Parent grid's per-breakpoint column count (when this block is itself a
     * tile inside another GDICarouselGridBlock). Needed to decide whether
     * left-side viewport bleed is safe at each breakpoint: if our ColSpan
     * matches/exceeds the parent's TilesPerRow at that breakpoint we span
     * the full parent row, so no left sibling exists to overlap. */
    const parentGridColumns = useGDIGridColumns()
    const isFullWidthInParent = (() => {
        if (!sectionGridColumn) return true // not nested → always treat as full width
        const colsObj =
            parentGridColumns && typeof parentGridColumns === 'object'
                ? parentGridColumns
                : {base: Number(parentGridColumns) || null}
        const spanObj =
            sectionColSpan && typeof sectionColSpan === 'object'
                ? sectionColSpan
                : {base: Number(sectionColSpan) || null}
        const bps = new Set([...Object.keys(colsObj), ...Object.keys(spanObj)])
        const out = {}
        bps.forEach((bp) => {
            const cols = Number(colsObj[bp]) || null
            const span = Number(spanObj[bp]) || null
            // unknown parent columns → assume there could be siblings (false)
            out[bp] = cols ? span >= cols : false
        })
        return out
    })()

    const productToggles = useProductTileToggles(ProductCarouselGridStylesPerViewType)

    /* Gate the rec tiles' enrich (represented-color) availability fetch by how
     * the tile surfaces stock (see `availabilityMode` in useProductTileToggles):
     * 'hover' waits until the shopper hovers the carousel (arming the batched
     * call once); 'off' never fetches (swatch tiles fetch their own per-color
     * availability lazily, and quick-view tiles don't need stock). */
    const [availabilityArmed, setAvailabilityArmed] = useState(false)
    const availabilityMode = productToggles?.availabilityMode
    const tileAvailabilityEnabled = availabilityMode === 'hover' && availabilityArmed
    const armAvailability = useCallback(() => {
        if (availabilityMode === 'hover') setAvailabilityArmed(true)
    }, [availabilityMode])

    /* Aggregate every content-area slot the block exposes. Product source
     * priority is dynamic source first, dynamic reference as the SKU/query for
     * that source, and manual products only when no dynamic product source is
     * configured. */
    const dynamicAIModel = dynamicSourceToAIModel(ProductCarouselGridDynamicContent)
    const authoredDynamicReference = normalizeProductSku(ProductCarouselGridDynamicContentReference)
    const pdpProductSku = isGDIPDPPageContext
        ? normalizeProductSku(contextPdpProductSku) || getPdpProductSku(pdpProduct)
        : null
    /* Product-scoped models (bought-together / shop-the-look / similars) need a
     * reference SKU. Off-PDP hosts that still have a product context supply one
     * through `dynamicProductReference` — e.g. the cart page passes its line-item
     * SKUs so a "Vicky - Bought Together" carousel authored on GDICartPage
     * resolves against the basket instead of silently rendering nothing. */
    const contextProductReference = normalizeProductSku(contextDynamicProductReference)
    const effectiveDynamicReference =
        dynamicAIModel === 'semantic-search'
            ? authoredDynamicReference
            : authoredDynamicReference || pdpProductSku || contextProductReference
    /* `vickyShopTheLookSource` picks VickyAI or SFCC's own product recommendations
     * for Shop The Look; suppress the VickyAI fetch when SFCC won. */
    const {
        tiles: sfccRecommendationTiles,
        isLoading: isSfccRecommendationLoading,
        isActive: usesSfccRecommendations
    } = useSfccRecommendationTiles(ProductCarouselGridDynamicContent, pdpProduct)
    const vickyDynamicContent = usesSfccRecommendations ? null : ProductCarouselGridDynamicContent
    const {tiles: vickyTiles, isLoading: isVickyLoading} = useVickyTiles(
        vickyDynamicContent,
        _recommendationData,
        effectiveDynamicReference,
        {availabilityEnabled: tileAvailabilityEnabled}
    )
    const recentlyViewedTiles = useRecentlyViewedTiles(ProductCarouselGridDynamicContent, {
        availabilityEnabled: tileAvailabilityEnabled
    })
    const {tiles: earlyAccessTiles, isLoading: isEarlyAccessLoading} = useEarlyAccessTiles(
        ProductCarouselGridDynamicContent
    )
    /* CTA reference overrides: when `CtaButtonDynamicContentReference` is
     * authored, its value is used as the category ID (for Sub-categories Links)
     * or product ID (for More to Discover) instead of the current page context. */
    const ctaRefCategoryId =
        CtaButtonDynamicContent === PLP_SUB_CATEGORIES_DYNAMIC_CONTENT
            ? normalizeProductSku(CtaButtonDynamicContentReference)
            : null
    const ctaRefProductId =
        CtaButtonDynamicContent === PDP_MORE_TO_DISCOVER_DYNAMIC_CONTENT
            ? normalizeProductSku(CtaButtonDynamicContentReference)
            : null
    const {data: referencedCtaCategory} = useCategory(
        {parameters: {id: ctaRefCategoryId || '', levels: 1}},
        {enabled: Boolean(ctaRefCategoryId)}
    )
    const {data: referencedCtaProduct} = useProduct(
        {parameters: {id: ctaRefProductId || ''}},
        {enabled: Boolean(ctaRefProductId)}
    )
    /* Measures the distance from the section wrapper's outer edges to the
     * viewport's edges so the inner scroll track can bleed out via measured
     * pixel values (avoids the `calc(50% - 50vw)` pitfalls with scrollbars
     * / ancestor padding). The measurement refresh key includes async data
     * sources (Vicky/Einstein product tiles, referenced category/product CTA
     * sources, active PLP/PDP context) so a carousel that initially renders
     * empty — then mounts after data arrives — re-measures once the wrapper
     * is actually in the DOM. Without this, an initially-empty carousel
     * (e.g. Sub-categories Links waiting for the category fetch) bails out
     * via `limited.length === 0`, never attaches its ref, and the bleed
     * stays at 0 even after content arrives. */
    const dynamicTileCount =
        vickyTiles.length +
        recentlyViewedTiles.length +
        earlyAccessTiles.length +
        sfccRecommendationTiles.length +
        (referencedCtaCategory?.categories?.length || 0) +
        (referencedCtaProduct?.c_discoverCategories ? 1 : 0) +
        (category?.categories?.length || 0) +
        (pdpProduct?.c_discoverCategories ? 1 : 0)
    const {
        ref: bleedRef,
        leftPx: bleedLeft,
        rightPx: bleedRight,
        scrollbarPx: bleedScrollbar
    } = useViewportBleed(dynamicTileCount)
    /* ProductCarouselGridManualContent is a plain array of product IDs/SKUs
     * (strings) authored in the CMS. Wrap each as a GDIProductTileBlock
     * pseudo-tile so renderComponentsSwitch picks it up exactly like a
     * Vicky-driven tile. */
    const manualProductTiles = Array.isArray(ProductCarouselGridManualContent)
        ? ProductCarouselGridManualContent.filter((id) => typeof id === 'string' && id).map(
              (id, i) => ({
                  __typename: 'GDIProductTileBlock',
                  ProductId: id,
                  ContentLink: {GuidValue: `manual-${id}-${i}`}
              })
          )
        : []
    const isEarlyAccess = isEarlyAccessSource(ProductCarouselGridDynamicContent)
    const hasDynamicProductSource =
        isRecentlyViewedSource(ProductCarouselGridDynamicContent) ||
        isEarlyAccess ||
        Boolean(dynamicAIModel)
    const dynamicProductTiles = (() => {
        if (isEarlyAccess) return earlyAccessTiles
        if (isRecentlyViewedSource(ProductCarouselGridDynamicContent)) return recentlyViewedTiles
        if (usesSfccRecommendations) return sfccRecommendationTiles
        return vickyTiles
    })()
    const productTiles = hasDynamicProductSource ? dynamicProductTiles : manualProductTiles

    /* Dynamic CTAs derived from the active PLP category. When
     * `CtaButtonDynamicContent === 'Sub-categories Links'` the carousel emits
     * one CTA for the parent category (always first) plus one per child
     * category. Falls back to `CtaButtonManualContent` when the dynamic
     * source is unset / unsupported / there's no PLP category in scope. */
    const buildSubcategoryCtaTile = (cat, suffix) => {
        if (!cat?.id || !cat?.name) return null
        /* `categoryUrlBuilder` reads `c_relativeUrl` (populated by SCAPI on
         * both the active category and its `categories` children) — same
         * source the rest of the storefront uses for category links.
         * Surface it via `resolvedUrl` so `useGetLinkData` returns the
         * SEO-friendly URL exactly like an SSR-resolved authored link. */
        const resolvedUrl = categoryUrlBuilder(cat)
        return {
            __typename: 'GDICtaBlock',
            ContentLink: {GuidValue: `dyn-cta-subcat-${cat.id}-${suffix}`},
            CtaText: cat.name,
            GDICtaBlockLink: [
                {
                    ContentLink: {
                        Expanded: {
                            __typename: 'GDILinkBlock',
                            LinkType: 'Category ID',
                            LinkReference: cat.id,
                            AriaLabel: cat.name,
                            resolvedUrl
                        }
                    }
                }
            ],
            GDICtaBlockStylesPerViewType: CtaButtonStylesPerViewType
        }
    }

    const buildPdpMoreToDiscoverCtaTile = (discoverCategory, index) => {
        const {id, name, url, name_fr_ca: nameFrCA, url_fr_ca: urlFrCA} = discoverCategory || {}
        const localizedName = isFrCA ? nameFrCA || name : name
        const localizedUrl = isFrCA ? urlFrCA || url : url
        if (!localizedName || (!id && !localizedUrl)) return null

        const key = id || `url-${index}`

        return {
            __typename: 'GDICtaBlock',
            ContentLink: {GuidValue: `dyn-cta-pdp-more-to-discover-${key}`},
            CtaText: localizedName,
            GDICtaBlockLink: [
                {
                    ContentLink: {
                        Expanded: {
                            __typename: 'GDILinkBlock',
                            LinkType: id ? 'Category ID' : 'Internal Link',
                            LinkReference: id || localizedUrl,
                            ...(localizedUrl && {resolvedUrl: localizedUrl}),
                            AriaLabel: localizedName
                        }
                    }
                }
            ],
            GDICtaBlockStylesPerViewType: CtaButtonStylesPerViewType
        }
    }

    const dynamicCtaTiles = (() => {
        if (CtaButtonDynamicContent === PDP_MORE_TO_DISCOVER_DYNAMIC_CONTENT) {
            const sourceProduct = referencedCtaProduct || pdpProduct
            return parseDiscoverCategories(sourceProduct?.c_discoverCategories)
                .map(buildPdpMoreToDiscoverCtaTile)
                .filter(Boolean)
        }

        if (CtaButtonDynamicContent !== PLP_SUB_CATEGORIES_DYNAMIC_CONTENT) return null
        const sourceCategory = referencedCtaCategory || category
        if (!sourceCategory) return null
        const children = Array.isArray(sourceCategory.categories) ? sourceCategory.categories : []
        // No subcategories → suppress the dynamic CTAs entirely (including
        // the parent tile) so the carousel doesn't render a lone parent
        // link as the only CTA.
        if (children.length === 0) return []
        /* Inside the mobile menu's GroupedCategories drawer, omit the current
         * category (parent) link and surface only the child subcategory
         * links. Elsewhere (e.g. a PLP) the parent link is shown first. */
        if (isInsideGroupedCategories) {
            return children
                .map((child, i) => buildSubcategoryCtaTile(child, `child-${i}`))
                .filter(Boolean)
        }
        return [
            buildSubcategoryCtaTile(sourceCategory, 'parent'),
            ...children.map((child, i) => buildSubcategoryCtaTile(child, `child-${i}`))
        ].filter(Boolean)
    })()

    const ctaTiles = dynamicCtaTiles || getAllExpandedFromContentArea(CtaButtonManualContent)

    /* Loyalty-aware filter for Exclusive Rewards reward tiles. The reward
     * tile renders `null` when there is no matching `tile-content-id` in
     * the sync-data payload, but the carousel still wraps every tile in a
     * slide `<Box>`. Drop those tiles up-front so the slot collapses
     * entirely instead of leaving an empty column in the grid. Tiles of
     * any other type — or reward tiles whose Type is not Exclusive
     * Rewards — pass through unchanged. Until loyalty data is ready we
     * skip these tiles too (they would render `null` anyway). */
    const {loyaltyData, isReady: isLoyaltyReady} = useLoyaltyData() || {}
    const loyaltyRewards = Array.isArray(loyaltyData?.rewards) ? loyaltyData.rewards : []
    const isHiddenExclusiveRewardTile = (tile) => {
        if (tile?.__typename !== 'GDIRewardTileBlock') return false
        if (tile?.Type !== REWARD_TYPE_EXCLUSIVE_REWARDS) return false
        if (!isLoyaltyReady) return true
        return !loyaltyRewards.some((reward) => reward?.['tile-content-id'] === tile?.ReferenceID)
    }

    /* Drop hidden exclusive-reward tiles up-front so an entitlement-hidden tile
     * leaves no empty slide `<Box>` behind (the block itself would render `null`,
     * but the carousel still wraps every tile in a slide wrapper). */
    const tiles = [
        ...getAllExpandedFromContentArea(ContentTiles),
        ...productTiles,
        ...ctaTiles,
        ...getAllExpandedFromContentArea(ProductCarouselGridContentTile)
    ].filter((tile) => !isHiddenExclusiveRewardTile(tile))
    const limited =
        typeof MaxItemsToShow === 'number' && MaxItemsToShow > 0
            ? tiles.slice(0, MaxItemsToShow)
            : tiles

    const carouselActive = currentDisplayType === 'Carousel'

    const {atStart, atEnd} = useEdgeState(scrollRef, {
        enabled: carouselActive,
        slidesLength: limited.length
    })
    /* When a nav arrow reaches an edge it becomes `visibility: hidden`. If that
     * arrow currently holds focus (the user just clicked it) the browser drops
     * focus. Inside a Chakra Drawer/Modal the focus-lock then snaps focus back
     * to the first focusable element — a product link inside the scroll track —
     * whose `scrollIntoView` resets scrollLeft and jumps the carousel to slide
     * 1. Keep focus on the opposite, still-visible arrow so the focus-lock has
     * nothing to recover and the scroll position is preserved. */
    useEffect(() => {
        if (!carouselActive || loop) return
        const active = typeof document !== 'undefined' ? document.activeElement : null
        if (atEnd && active === rightArrowRef.current && leftArrowRef.current) {
            leftArrowRef.current.focus({preventScroll: true})
        } else if (atStart && active === leftArrowRef.current && rightArrowRef.current) {
            rightArrowRef.current.focus({preventScroll: true})
        }
    }, [atStart, atEnd, carouselActive, loop])
    useAutoplay(scrollRef, {
        enabled: autoplay && carouselActive,
        delay,
        loop,
        tilesToScroll,
        slidesLength: limited.length
    })
    useDragScroll(scrollRef, {enabled: draggable && carouselActive, refreshKey: limited.length})
    useInfiniteLoop(scrollRef, {
        enabled: loop && carouselActive && limited.length > 1,
        slidesLength: limited.length
    })

    if (hideStyles.doNotLoadComponent) return null
    /* Bail only when there are no tiles AND no section-level styling authored.
     * The section can be a standalone backdrop (background color/media) even
     * with no tiles, so we keep rendering the wrapper in that case. */
    const hasSectionStyling =
        sectionBackgroundColor || sectionBackgroundMedia || hasMaxWidth || sectionGridColumn
    if (limited.length === 0 && !hasSectionStyling) {
        if (
            (isVickyLoading || isEarlyAccessLoading || isSfccRecommendationLoading) &&
            hasDynamicProductSource
        ) {
            const skeletonColumns = maxResponsive(tilesPerRow) || 2
            const skeletonCount = MaxItemsToShow
                ? Math.min(MaxItemsToShow, skeletonColumns)
                : skeletonColumns
            return (
                <CarouselGridSkeleton
                    count={skeletonCount}
                    itemWidth={tilesPerRowAuto ? undefined : buildSlideWidth(tilesPerRow, tilesGap)}
                    gap={tilesGap}
                />
            )
        }
        return null
    }

    const keyOf = (tile, idx) => tile?.ContentLink?.GuidValue || `gdi-cg-${idx}`
    /* Gap-aware slide width: subtracts (N-1)*gap from 100% so wrapped rows
     * fit exactly N tiles without flow-overflow forcing single-column wrap.
     * In auto mode (TilesPerRow unauthored) slides size to their intrinsic
     * content width and the row scrolls when total width > viewport. */
    const slideWidth = tilesPerRowAuto ? undefined : buildSlideWidth(tilesPerRow, tilesGap)

    /* Cloned head/tail bands for seamless infinite loop — only at carousel bp.
     * Ceil the largest TilesPerRow so fractional values still produce enough
     * clones for backward nav not to clamp at scrollLeft=0. Skip entirely in
     * auto mode: cloning intrinsically-sized tiles would scramble the natural
     * scroll length the loop hook measures. */
    const cloneCount =
        loop && limited.length > 1 && carouselActive && !tilesPerRowAuto
            ? Math.max(1, Math.ceil(maxResponsive(tilesPerRow)))
            : 0
    const headClones = cloneCount > 0 ? limited.slice(-cloneCount) : []
    const tailClones = cloneCount > 0 ? limited.slice(0, cloneCount) : []
    const renderList = [...headClones, ...limited, ...tailClones]

    const scroll = (direction = 1) => {
        const node = scrollRef.current
        if (!node) return
        const stride = getSlideStride(node) * tilesToScroll
        node.scrollBy({left: direction * stride, behavior: 'smooth'})
    }

    /* CSS handles the structural switch between Grid and Carousel — no need
     * to swap component types between breakpoints. */
    const flexWrap = mapResponsiveValue(displayTypeByBp, (dt) =>
        dt === 'Carousel' ? 'nowrap' : 'wrap'
    )
    const overflowX = mapCarouselInteraction(displayTypeByBp, draggableByBp, (dt, canDrag) =>
        dt === 'Carousel' ? (canDrag ? 'auto' : 'hidden') : 'visible'
    )
    const overflowY = mapResponsiveValue(displayTypeByBp, (dt) =>
        dt === 'Carousel' ? 'hidden' : 'visible'
    )
    const scrollSnapType = mapResponsiveValue(displayTypeByBp, (dt) =>
        dt === 'Carousel' ? 'x mandatory' : 'none'
    )
    const cursor = mapCarouselInteraction(displayTypeByBp, draggableByBp, (dt, canDrag) =>
        dt === 'Carousel' && canDrag ? 'grab' : 'auto'
    )
    const activeCursor = mapCarouselInteraction(displayTypeByBp, draggableByBp, (dt, canDrag) =>
        dt === 'Carousel' && canDrag ? 'grabbing' : 'auto'
    )

    /* When this block is a draggable carousel at a given breakpoint, force
     * any nested product-tile image-scroller (data-pt-media-scroller) to
     * give up horizontal scrolling at that breakpoint. The browser routes
     * a touch pan to the nearest ancestor that can scroll on that axis, so
     * removing the inner's horizontal overflow lets the parent carousel
     * receive the swipe instead of the tile's own image row.
     *
     * Note: `touch-action: pan-y` on the inner does NOT work here — per
     * spec, touch-action values intersect down the ancestor chain
     * (pan-y ∩ auto = pan-y), which would block the horizontal pan
     * entirely instead of delegating it. Suppressing overflow is the
     * correct mechanism. */
    const innerScrollerOverflowX = mapCarouselInteraction(
        displayTypeByBp,
        draggableByBp,
        (dt, canDrag) => (dt === 'Carousel' && canDrag ? 'hidden' : 'auto')
    )

    /* Same condition as `innerScrollerOverflowX`, as a boolean the tile can act
     * on: where this block takes the horizontal swipe, the tile's own media row
     * is unreachable, so the tile skips building it instead of rendering (and
     * fetching) alternates nobody can scroll to. */
    const tileMediaScrollDisabled = mapCarouselInteraction(
        displayTypeByBp,
        draggableByBp,
        (dt, canDrag) => dt === 'Carousel' && canDrag
    )

    const productTogglesForTiles = {...productToggles, tileMediaScrollDisabled}

    /* In Carousel mode, `justify-content: center` (or `flex-end`) on a
     * horizontally-scrolling row pushes the overflow into the unreachable
     * direction — the first slides end up before `scrollLeft: 0` and can
     * never be brought into view. The CSS `safe` keyword makes the browser
     * fall back to `flex-start` when content overflows, so all slides
     * remain reachable while still centering when there's slack. Grid mode
     * doesn't scroll, so it keeps the raw value. */
    const safeJustify = (() => {
        if (!carouselActive) return justify
        const wrap = (v) => (v === 'center' || v === 'flex-end' ? `safe ${v}` : v)
        if (typeof justify === 'object' && justify) {
            return Object.fromEntries(Object.entries(justify).map(([bp, v]) => [bp, wrap(v)]))
        }
        return wrap(justify)
    })()

    /* ReverseOrderOnDesktop applies the RTL trick only when desktop is in
     * Grid mode — reversing item order in a horizontally-scrolling carousel
     * would flip the scroll direction in a confusing way. */
    const reverseSx =
        ReverseOrderOnDesktop && !carouselActive
            ? {'@media (min-width: 1024px)': {direction: 'rtl', '& > *': {direction: 'ltr'}}}
            : undefined

    /* Section-level props are split in two: outer props only handle
     * placement (parent-grid cell when nested), while inner props carry
     * the visual + sizing styles (background, maxWidth, padding/margin,
     * centering / fluid bleed) and the `bleedRef` for viewport-bleed
     * measurement. The section title renders INSIDE the styled section
     * so it inherits background, padding/margin, maxWidth and fluid bleed
     * along with the carousel content. */
    const sectionOuterProps = {
        gridColumn: sectionGridColumn || undefined,
        /* When this block is itself a tile in a parent grid (authored
         * `ColSpan`, surfaced as `sectionGridColumn`), stretch to fill the
         * parent slide so children with vertical positioning (e.g. a content
         * tile authored with `ContentVerticalPosition: Bottom`) have a real
         * height to anchor against. The outer slide wrapper already passes
         * `flex: 1` to its child, so `height: 100%` here resolves to the
         * row height. Become a flex column so the styled section can
         * flex-grow to fill the row. */
        ...(sectionGridColumn ? {height: '100%', display: 'flex', flexDirection: 'column'} : null)
    }

    /* Build per-breakpoint style values so a config that mixes `full-bleed` on
     * one breakpoint with `max-width-*` on another renders correctly. Without
     * this, the scalar `isFluid` / `isFullBleed` flags would force 100vw +
     * `calc(50% - 50vw)` on every breakpoint as soon as any one used
     * `full-bleed`. Falls back to the scalar flags when the hook hasn't
     * provided per-breakpoint maps (e.g. legacy test mocks). */
    const RESPONSIVE_BPS = ['base', 'md', 'lg']
    const scalarMap = (v) => ({base: v, md: v, lg: v})
    /* `forceCarouselBleed` (from the dynamic-content context) makes the
     * carousel render with the fluid-container bleed at every breakpoint,
     * regardless of the authored SectionWidth. This reproduces the mobile
     * "start gutter → edge-to-edge bleed while scrolling → end gutter"
     * behaviour on desktop too: forcing fluid gives `width:100vw` +
     * `mx:calc(50% - 50vw)` on the section and `FLUID_INSET` padding on the
     * inner scroll track, so tile 1 snaps at the page gutter and slides bleed
     * to the viewport edges as they scroll. Full-bleed / max-width are
     * suppressed so they can't zero out the inset or constrain the width. */
    const safeFluidByBp = contextForceCarouselBleed
        ? scalarMap(true)
        : isFluidByBp || scalarMap(isFluid)
    const safeFullBleedByBp = contextForceCarouselBleed
        ? scalarMap(false)
        : isFullBleedByBp || scalarMap(isFullBleed)
    const safeMaxWidthByBp = contextForceCarouselBleed
        ? scalarMap(false)
        : hasMaxWidthByBp || scalarMap(hasMaxWidth)
    const buildPerBp = (fn) => Object.fromEntries(RESPONSIVE_BPS.map((bp) => [bp, fn(bp)]))
    const maxWidthByBp = buildPerBp((bp) =>
        safeMaxWidthByBp[bp] && sectionWidth && typeof sectionWidth === 'object'
            ? sectionWidth[bp]
            : safeMaxWidthByBp[bp]
            ? sectionWidth
            : undefined
    )
    const mxByBp = buildPerBp((bp) =>
        safeFluidByBp[bp]
            ? /* `100vw` includes the vertical scrollbar, so a plain
               * `calc(50% - 50vw)` pulls the fluid section ~scrollbar/2px past
               * the visible left edge — making the FLUID_INSET gutter render
               * narrower than authored (e.g. 16px instead of 24px). Adding
               * `scrollbarPx/2` back lands the section's left edge exactly on
               * the visible viewport edge, so the inner track inset is the
               * true FLUID_INSET on both sides. */
              bleedScrollbar
                ? `calc(50% - 50vw + ${bleedScrollbar / 2}px)`
                : 'calc(50% - 50vw)'
            : safeMaxWidthByBp[bp]
            ? 'auto'
            : undefined
    )
    const widthByBp = buildPerBp((bp) =>
        safeFluidByBp[bp]
            ? /* Match the scrollbar compensation applied to `mx` above so the
               * section spans the visible viewport (clientWidth) rather than
               * overshooting under the scrollbar by `scrollbarPx`. */
              bleedScrollbar
                ? `calc(100vw - ${bleedScrollbar}px)`
                : '100vw'
            : safeMaxWidthByBp[bp]
            ? /* A capped section centres via `mx: auto`, which only resolves
               * against a definite width — keep `100%` so `max-width` clamps
               * it and the auto margins split the remainder. */
              '100%'
            : /* `100%` ignores the box's own margins, so an authored
               * horizontal margin (e.g. `64px 24px`) would push the section
               * past its container's right edge and shift its whole subtree
               * right by the left margin. That offset de-centres every
               * descendant, breaking the `calc(50% - 50vw)` fluid bleed below
               * (which assumes a centred containing block). `auto` sizes the
               * box to the container MINUS its margins, keeping it centred. */
              'auto'
    )
    /* `FLUID_INSET` is itself a responsive object (e.g. {base:'16px', lg:'24px'}),
     * so we must resolve it to a per-bp scalar before placing it into another
     * responsive map — otherwise Chakra sees a nested object value and ignores
     * the prop. Mobile-first inheritance: md falls back to base, lg falls back
     * to md ← base. */
    const fluidInsetAt = (bp) => {
        if (FLUID_INSET[bp] !== undefined) return FLUID_INSET[bp]
        if (bp === 'lg' && FLUID_INSET.md !== undefined) return FLUID_INSET.md
        return FLUID_INSET.base
    }
    /* `carouselPeekInset` (from the dynamic-content context) restores the
     * peek gutter on the inner scroll track for a non-fluid partial-tile
     * Carousel whose viewport bleed has been disabled (e.g. inside a
     * right-side drawer/modal). Without it the suppressed bleed leaves tile 1
     * flush against the container edge. */
    const applyPeekInset = contextCarouselPeekInset && carouselActive && hasPartialTilePeek
    const innerPxByBp = buildPerBp((bp) =>
        safeFullBleedByBp[bp] ? 0 : safeFluidByBp[bp] ? fluidInsetAt(bp) : 0
    )

    /* Title gutter. Any fluid section (`fluid-container` OR `full-bleed`) is
     * bled to the viewport edges, so without an inset the title would sit
     * flush against the screen edge. The title always keeps the page gutter,
     * which for `fluid-container` lines it up with tile 1 and for
     * `full-bleed` deliberately does not — there the tiles touch the edges
     * while the title stays on the page grid. Non-fluid sections keep their
     * authored alignment. */
    const titlePxByBp = buildPerBp((bp) => (safeFluidByBp[bp] ? fluidInsetAt(bp) : undefined))

    /* Resolve a responsive value to a per-bp scalar with mobile-first fallback
     * (lg ← md ← base). Used to read the authored padding/margin shorthand for
     * a given breakpoint. */
    const resolveResponsiveAt = (responsive, bp) =>
        responsive && typeof responsive === 'object'
            ? responsive[bp] ?? responsive.md ?? responsive.base
            : responsive

    /* Peek gutter width per side, sourced from the authored padding/margin so
     * it's CMS-driven. Padding-left/right takes precedence over
     * margin-left/right. An unauthored side gets `0` (no gutter) — nothing is
     * hardcoded, so no peek appears unless the CMS explicitly sets one. */
    const resolvePeekSide = (bp, side) => {
        const pad = parseBoxSides(resolveResponsiveAt(sectionPadding, bp))
        const mar = parseBoxSides(resolveResponsiveAt(sectionMargin, bp))
        const authoredPad = pad && hasValue(pad[side]) ? pad[side] : undefined
        const authoredMar = mar && hasValue(mar[side]) ? mar[side] : undefined
        return authoredPad ?? authoredMar ?? 0
    }
    const peekLeftByBp = applyPeekInset
        ? buildPerBp((bp) =>
              !safeFullBleedByBp[bp] && !safeFluidByBp[bp]
                  ? resolvePeekSide(bp, 'left')
                  : innerPxByBp[bp]
          )
        : null
    const peekRightByBp = applyPeekInset
        ? buildPerBp((bp) =>
              !safeFullBleedByBp[bp] && !safeFluidByBp[bp]
                  ? resolvePeekSide(bp, 'right')
                  : innerPxByBp[bp]
          )
        : null

    /* When the authored left/right gutter is repurposed as the inner-track
     * peek (above), strip it from the section box so it isn't applied twice —
     * keep only the vertical (top/bottom) components on the section. */
    const stripHorizontalSides = (responsive) => {
        const strip = (val) => {
            const sides = parseBoxSides(val)
            return sides ? `${sides.top} 0 ${sides.bottom} 0` : val
        }
        if (!responsive) return responsive
        if (typeof responsive !== 'object') return strip(responsive)
        const out = {}
        Object.entries(responsive).forEach(([bp, val]) => {
            out[bp] = strip(val)
        })
        return out
    }
    const sectionMarginResolved = applyPeekInset
        ? stripHorizontalSides(sectionMargin)
        : sectionMargin
    const sectionPaddingResolved = applyPeekInset
        ? stripHorizontalSides(sectionPadding)
        : sectionPadding

    const sectionInnerProps = {
        position: 'relative',
        backgroundColor: sectionBackgroundColor || undefined,
        maxWidth: maxWidthByBp,
        // `mx` handles system-level horizontal positioning (fluid bleed / centering).
        // `sectionMargin` carries author-defined margins; Chakra's mx (marginLeft +
        // marginRight) overrides the horizontal component of the `margin` shorthand
        // per CSS cascade, so centering/bleed still applies to the horizontal axis.
        margin: sectionMarginResolved || undefined,
        padding: sectionPaddingResolved || undefined,
        mx: mxByBp,
        width: widthByBp,
        /* When nested in a parent grid cell, grow to fill the available
         * height left by the title above, and become a flex column so the
         * inner scroll track can flex-grow to fill us. */
        ...(sectionGridColumn
            ? {flex: '1 1 auto', minHeight: 0, display: 'flex', flexDirection: 'column'}
            : null)
    }

    /* Background media layer — absolutely positioned behind contents.
     * Rendered via the content switch so any media block (image, video,
     * etc.) is supported. */
    const sectionBackgroundLayer = sectionBackgroundMedia ? (
        <Box
            data-gdi-section-bg
            position="absolute"
            inset={0}
            zIndex={0}
            pointerEvents="none"
            overflow="hidden"
            sx={{
                /* Force any rendered media (image/video) to cover the section. */
                '& > *, & img, & video, & picture, & picture > img': {
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover'
                }
            }}
        >
            {renderComponentsSwitch(sectionBackgroundMedia)}
        </Box>
    ) : null

    return (
        <GDIHoverRevealContext.Provider value={!!DisplayTextAndCTAsOnHover}>
            <GDIGridColumnsContext.Provider value={tilesPerRow}>
                <GDIProductTogglesContext.Provider value={productTogglesForTiles}>
                    <Box
                        data-block-type="GDICarouselGridBlock"
                        sx={hideStyles.sx}
                        onMouseEnter={armAvailability}
                        onFocusCapture={armAvailability}
                        onTouchStart={armAvailability}
                        {...sectionOuterProps}
                    >
                        <Box ref={bleedRef} {...sectionInnerProps}>
                            {sectionBackgroundLayer}
                            {/* Title renders INSIDE the styled section so it
                             * inherits the section's background, padding/margin,
                             * maxWidth and fluid bleed alongside the carousel
                             * content. Each title block carries its own
                             * per-breakpoint `displayByBp` (computed from
                             * per-view-type style entries) so authors can show
                             * different content per breakpoint. */}
                            {sectionTitle?.length > 0 &&
                                sectionTitle.map(({key, block, displayByBp}, idx) => (
                                    <Box
                                        key={key || `section-title-${idx}`}
                                        display={displayByBp}
                                        position="relative"
                                        zIndex={1}
                                        /* Keep the title on the page gutter
                                         * whenever the section is bled to the
                                         * viewport, instead of letting it sit
                                         * flush against the screen edge — see
                                         * `titlePxByBp`. */
                                        px={titlePxByBp}
                                    >
                                        {renderComponentsSwitch(block)}
                                    </Box>
                                ))}
                            <Flex
                                ref={scrollRef}
                                gap={tilesGap}
                                justifyContent={safeJustify}
                                alignItems={alignItems}
                                position="relative"
                                zIndex={1}
                                /* Inner padding only when the section is fluid. In
                                 * Carousel mode this is the bleed affordance: tiles
                                 * scroll past the gutter and peek in from the
                                 * viewport edge. In Grid mode it just keeps the
                                 * row visually anchored at FLUID_INSET from each
                                 * edge. `full-bleed` gets zero padding so tiles
                                 * touch the actual viewport edges. When
                                 * `applyPeekInset` restores a CMS-driven peek
                                 * gutter, per-side padding (pl/pr) is used
                                 * instead so the left/right gutters can differ. */
                                px={applyPeekInset ? undefined : innerPxByBp}
                                pl={applyPeekInset ? peekLeftByBp : undefined}
                                pr={applyPeekInset ? peekRightByBp : undefined}
                                /* Offsets the snap target so tile 1 snaps to the
                                 * padding edge, not the container edge. */
                                scrollPaddingX={applyPeekInset ? undefined : innerPxByBp}
                                scrollPaddingLeft={applyPeekInset ? peekLeftByBp : undefined}
                                scrollPaddingRight={applyPeekInset ? peekRightByBp : undefined}
                                sx={{
                                    flexWrap,
                                    overflowX,
                                    overflowY,
                                    scrollSnapType,
                                    cursor,
                                    '&:active': {cursor: activeCursor},
                                    WebkitOverflowScrolling: 'touch',
                                    scrollbarWidth: 'none',
                                    '&::-webkit-scrollbar': {display: 'none'},
                                    /* Edge bleed for constrained (non-fluid)
                                     * carousels — uses MEASURED pixel offsets
                                     * (see useViewportBleed) instead of
                                     * `calc(50% - 50vw)` so it's robust against
                                     * vertical scrollbars and ancestor padding.
                                     * The negative margins extend the scroll
                                     * track to the actual viewport edges; the
                                     * matching `padding-*` (and
                                     * `scroll-padding-*`) make the first/last
                                     * snap position align with the original
                                     * page-content boundary, so on initial
                                     * load tile 1 is flush with surrounding
                                     * page content and the right gutter only
                                     * re-appears when scrolled to the end.
                                     * While scrolling, outgoing/incoming tiles
                                     * travel all the way to the viewport edges.
                                     * App-root `overflow-x: clip`
                                     * (theme/foundations/styles.js) absorbs
                                     * the overflow so this never produces a
                                     * page-level horizontal scrollbar.
                                     *
                                     * When the section sits in a parent grid cell
                                     * (authored ColSpan, surfaced here as
                                     * `sectionGridColumn`), suppress only the LEFT
                                     * bleed — otherwise slides would scroll over
                                     * sibling columns to its left. The right
                                     * bleed is preserved so a right-aligned cell
                                     * (e.g. ColSpan=3 next to a ColSpan=1) can
                                     * still extend to the viewport edge. */
                                    ...(carouselActive &&
                                    !isFluid &&
                                    hasPartialTilePeek &&
                                    !contextDisableViewportBleed &&
                                    !contextForceCarouselBleed
                                        ? (() => {
                                              /* Per-breakpoint left bleed: only
                                               * suppress where the nested block
                                               * actually has a sibling to its
                                               * left at that breakpoint. When
                                               * `isFullWidthInParent[bp]` is
                                               * true the block spans the entire
                                               * parent row (e.g. a sibling is
                                               * hidden on mobile) and bleeding
                                               * is safe. The right bleed is
                                               * always applied — sibling on the
                                               * right is fine, the carousel
                                               * scrolls into that space rather
                                               * than across it. */
                                              const buildLeft = (px) => {
                                                  if (isFullWidthInParent === true) return px
                                                  const out = {}
                                                  Object.entries(isFullWidthInParent).forEach(
                                                      ([bp, full]) => {
                                                          out[bp] = full ? px : 0
                                                      }
                                                  )
                                                  return out
                                              }
                                              return {
                                                  marginLeft: buildLeft(`-${bleedLeft}px`),
                                                  marginRight: `-${bleedRight}px`,
                                                  paddingLeft: buildLeft(`${bleedLeft}px`),
                                                  paddingRight: `${bleedRight}px`,
                                                  scrollPaddingLeft: buildLeft(`${bleedLeft}px`),
                                                  scrollPaddingRight: `${bleedRight}px`
                                              }
                                          })()
                                        : null),
                                    /* When nested in a parent grid cell, grow
                                     * the scroll track to fill the wrapper's
                                     * height so a single inner tile (or a row
                                     * of stretched tiles) can be aligned
                                     * vertically inside the row, not just
                                     * top-anchored to its content height. */
                                    ...(sectionGridColumn
                                        ? {flex: '1 1 auto', minHeight: 0}
                                        : null),
                                    /* Delegate horizontal touch from any nested
                                     * product-tile image scroller to this
                                     * carousel — see `innerScrollerOverflowX`
                                     * above. */
                                    '[data-pt-media-scroller="true"]': {
                                        overflowX: innerScrollerOverflowX
                                    },
                                    ...reverseSx
                                }}
                            >
                                {renderList.map((tile, idx) => {
                                    const meta = slideMeta(
                                        idx,
                                        tile,
                                        cloneCount,
                                        limited.length,
                                        keyOf
                                    )
                                    /* If the tile itself is hidden on every breakpoint we'd otherwise
                                     * render an empty slot here (still occupying width and counting as
                                     * a slide). Respect the child's HideBlockOn at the wrapper level
                                     * so it disappears entirely instead of leaving an empty column. */
                                    const tileHide = getGDIHideStyles(
                                        tile?.HideBlockOn || tile?.HideOnDevices
                                    )
                                    if (tileHide.doNotLoadComponent) return null
                                    /* Per-breakpoint display: 'none' where the tile is hidden, 'flex'
                                     * otherwise (wrapper still needs to be a flex column when visible
                                     * so its child can grow to fill the row height). */
                                    const slideDisplay = tileHide.display
                                        ? Object.fromEntries(
                                              Object.entries(tileHide.display).map(([bp, v]) => [
                                                  bp,
                                                  v === 'none' ? 'none' : 'flex'
                                              ])
                                          )
                                        : 'flex'
                                    /* Honour an authored `ColSpan` on the tile itself by widening
                                     * just this slide. The parent track is flex (not CSS Grid), so
                                     * a child's `gridColumn: span N` is silently ignored — instead
                                     * we compute the equivalent flex width here. Auto mode (no
                                     * authored TilesPerRow) keeps slides at their intrinsic content
                                     * width and ColSpan is meaningless in that case. */
                                    const tileColSpan = tilesPerRowAuto
                                        ? null
                                        : getTileColSpan(tile)
                                    const tileSlideWidth = tileColSpan
                                        ? buildSpanWidth(tileColSpan, tilesPerRow, tilesGap)
                                        : slideWidth
                                    /* Apply fallback styles for tiles that have no
                                     * per-tile styles authored.
                                     * - ContentTilesStyles → GDIContentTileBlock
                                     * - CtaButtonStylesPerViewType → GDICtaBlock */
                                    let effectiveTile = tile
                                    if (
                                        tile?.__typename === 'GDIContentTileBlock' &&
                                        ContentTilesStyles?.length > 0 &&
                                        !(tile.GDIContentTileBlockStylesPerViewType?.length > 0)
                                    ) {
                                        effectiveTile = {
                                            ...tile,
                                            GDIContentTileBlockStylesPerViewType: ContentTilesStyles
                                        }
                                    } else if (
                                        tile?.__typename === 'GDICtaBlock' &&
                                        CtaButtonStylesPerViewType?.length > 0
                                    ) {
                                        const tileStyles = tile.GDICtaBlockStylesPerViewType || []
                                        /* Merge parent CtaButtonStylesPerViewType with the tile's own
                                         * styles. For each ViewTypesToApply entry in the parent, tile
                                         * values win for non-empty fields; the parent fills in the rest.
                                         * Tile entries whose ViewTypesToApply has no parent counterpart
                                         * are kept as-is. */
                                        const parentViewTypes = new Set(
                                            CtaButtonStylesPerViewType.map(
                                                (s) => s.ViewTypesToApply
                                            )
                                        )
                                        const mergedStyles = CtaButtonStylesPerViewType.map(
                                            (parentEntry) => {
                                                const tileEntry =
                                                    tileStyles.find(
                                                        (s) =>
                                                            s.ViewTypesToApply ===
                                                            parentEntry.ViewTypesToApply
                                                    ) || {}
                                                return Object.fromEntries(
                                                    Object.entries(parentEntry).map(
                                                        ([key, parentVal]) => [
                                                            key,
                                                            tileEntry[key] != null &&
                                                            tileEntry[key] !== ''
                                                                ? tileEntry[key]
                                                                : parentVal
                                                        ]
                                                    )
                                                )
                                            }
                                        )
                                        const extraTileStyles = tileStyles.filter(
                                            (s) => !parentViewTypes.has(s.ViewTypesToApply)
                                        )
                                        effectiveTile = {
                                            ...tile,
                                            GDICtaBlockStylesPerViewType: [
                                                ...mergedStyles,
                                                ...extraTileStyles
                                            ]
                                        }
                                    }
                                    return (
                                        <Box
                                            key={meta.key}
                                            data-real-index={
                                                meta.realIdx === null ? undefined : meta.realIdx
                                            }
                                            aria-hidden={meta.realIdx === null ? true : undefined}
                                            flex="0 0 auto"
                                            width={tileSlideWidth}
                                            display={slideDisplay}
                                            /* Make the wrapper a flex column and have its single child grow
                                             * to fill the available height. The parent Flex stretches each
                                             * wrapper to the tallest sibling's height (default
                                             * align-items: stretch); without this, the rendered child
                                             * (GDIContentTileBlock etc.) keeps its content height and
                                             * leaves empty space below. */
                                            sx={{
                                                scrollSnapAlign: 'start',
                                                flexDirection: 'column',
                                                '&:empty': {display: 'none'},
                                                /* Stretch every child EXCEPT CTAs to fill the slide
                                                 * height (so content tiles match sibling heights).
                                                 * CTAs are intrinsically sized — applying `flex: 1`
                                                 * to them would also grow them vertically, breaking
                                                 * the button's natural height. */
                                                '& > *:not([data-block-type="GDICtaBlock"])': {
                                                    flex: 1,
                                                    minHeight: 0
                                                },
                                                /* CTAs ship with `alignSelf: 'flex-start'` baked in
                                                 * (see gdiCtaBlock) so they don't stretch to fill the
                                                 * slide. That means the row-level `justify-content`
                                                 * can't realign them when there's no slack along the
                                                 * main axis (e.g. TilesPerRow=1). Override per-slide
                                                 * here based on ContentJustification. */
                                                '& > [data-block-type="GDICtaBlock"]': {
                                                    alignSelf: ctaAlignSelf,
                                                    maxWidth: '100%'
                                                }
                                            }}
                                        >
                                            {renderComponentsSwitch(effectiveTile)}
                                        </Box>
                                    )
                                })}
                            </Flex>

                            {showArrows && limited.length > 1 && carouselActive && (
                                <>
                                    <ArrowButton
                                        label="Scroll left"
                                        onClick={() => scroll(-1)}
                                        side="left"
                                        hidden={!loop && atStart}
                                        icon={<ChevronLeftIcon color="black" />}
                                        sx={arrowSx}
                                        display={arrowDisplay}
                                        buttonRef={leftArrowRef}
                                    />
                                    <ArrowButton
                                        label="Scroll right"
                                        onClick={() => scroll(1)}
                                        side="right"
                                        hidden={!loop && atEnd}
                                        icon={<ChevronRightIcon color="black" />}
                                        sx={arrowSx}
                                        display={arrowDisplay}
                                        buttonRef={rightArrowRef}
                                    />
                                </>
                            )}
                        </Box>
                    </Box>
                </GDIProductTogglesContext.Provider>
            </GDIGridColumnsContext.Provider>
        </GDIHoverRevealContext.Provider>
    )
}

GDICarouselGridBlock.propTypes = {
    HideOnDevices: PropTypes.string,
    HideBlockOn: PropTypes.string,
    ContentTiles: PropTypes.array,
    _contentLinkId: PropTypes.number,
    CtaButtonDynamicContent: PropTypes.string,
    CtaButtonDynamicContentReference: PropTypes.string,
    CtaButtonManualContent: PropTypes.array,
    CtaButtonStylesPerViewType: PropTypes.array,
    ProductCarouselGridManualContent: PropTypes.array,
    ProductCarouselGridContentTile: PropTypes.array,
    ProductCarouselGridDynamicContent: PropTypes.string,
    ProductCarouselGridDynamicContentReference: PropTypes.string,
    GDICarouselGridBlockStylesPerViewType: PropTypes.array,
    ProductCarouselGridStylesPerViewType: PropTypes.array,
    ReverseOrderOnDesktop: PropTypes.bool,
    MaxItemsToShow: PropTypes.number,
    DisplayTextAndCTAsOnHover: PropTypes.bool,
    ContentTilesStyles: PropTypes.array,
    GDICarouselGridBlockAudienceTargeting: PropTypes.object,
    _recommendationData: PropTypes.object
}

export default GDICarouselGridBlock
